import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  RiArrowRightLine,
  RiCheckLine,
  RiCloseLine,
  RiImageAddLine,
  RiAddLine,
  RiShareForwardLine,
} from "@remixicon/react";
import { cn } from "@/lib/utils";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";

type SetupChecklistProps = {
  storeId: string;
  storeLink: string;
  hasLogo: boolean;
  hasProducts: boolean;
  hasTraffic: boolean;
  canEditStore: boolean;
  canAddProducts: boolean;
};

const storageKey = (storeId: string, key: string) => `sqs:setup:${storeId}:${key}`;

export function SetupChecklist({
  storeId,
  storeLink,
  hasLogo,
  hasProducts,
  hasTraffic,
  canEditStore,
  canAddProducts,
}: SetupChecklistProps) {
  const [dismissed, setDismissed] = useState(true);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(storageKey(storeId, "dismissed")) === "1");
    setShared(localStorage.getItem(storageKey(storeId, "shared")) === "1");
  }, [storeId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(storeLink);
      localStorage.setItem(storageKey(storeId, "shared"), "1");
      setShared(true);
      toast.success("Link copied, paste it in your bio or status");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  const steps = [
    {
      id: "logo",
      title: "Add your logo",
      body: "Make your sales link instantly recognisable.",
      done: hasLogo,
      icon: RiImageAddLine,
      visible: canEditStore,
      action: (
        <Link to="/dashboard/settings#store" className="inline-flex items-center gap-1">
          Upload logo <RiArrowRightLine className="h-3.5 w-3.5" />
        </Link>
      ),
    },
    {
      id: "product",
      title: "Add your first product",
      body: "Photos, price, and variants so buyers stop asking in DMs.",
      done: hasProducts,
      icon: RiAddLine,
      visible: canAddProducts,
      action: (
        <Link to="/dashboard/products/new" className="inline-flex items-center gap-1">
          Add product <RiArrowRightLine className="h-3.5 w-3.5" />
        </Link>
      ),
    },
    {
      id: "share",
      title: "Share your store link",
      body: "Drop it in your Instagram bio, WhatsApp status, or TikTok.",
      done: shared || hasTraffic,
      icon: RiShareForwardLine,
      visible: Boolean(storeLink),
      action: (
        <button type="button" onClick={copyLink} className="inline-flex items-center gap-1">
          Copy link <RiArrowRightLine className="h-3.5 w-3.5" />
        </button>
      ),
    },
  ].filter((step) => step.visible);

  const completed = steps.filter((step) => step.done).length;
  if (dismissed || steps.length === 0 || completed === steps.length) return null;

  const progress = Math.round((completed / steps.length) * 100);

  return (
    <section className={cn(dashboardStyles.sectionCard, "relative overflow-hidden")}>
      <div
        className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#EDE4FB] blur-3xl"
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-4">
        <div>
          <p className={dashboardStyles.sectionTitle}>Get your store ready</p>
          <p className="mt-0.5 text-xs font-medium text-muted-foreground">
            {completed} of {steps.length} done · a few minutes to finish
          </p>
        </div>
        <button
          type="button"
          aria-label="Dismiss setup guide"
          onClick={() => {
            localStorage.setItem(storageKey(storeId, "dismissed"), "1");
            setDismissed(true);
          }}
          className="-mr-1.5 -mt-1.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground"
        >
          <RiCloseLine className="h-4 w-4" />
        </button>
      </div>

      <div
        className="relative mt-4 h-1.5 overflow-hidden rounded-full bg-[#F0F0EC]"
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${Math.max(progress, 4)}%` }}
        />
      </div>

      <ol className="relative mt-4 grid gap-2 sm:grid-cols-3">
        {steps.map((step) => (
          <li
            key={step.id}
            className={cn(
              "flex flex-col rounded-2xl border p-3.5 transition-colors",
              step.done
                ? "border-transparent bg-[#F5F5F3]"
                : "border-[#EBEBEB] bg-white",
            )}
          >
            <span
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-xl",
                step.done ? "bg-emerald-500 text-white" : "bg-[#EDE4FB] text-primary",
              )}
            >
              {step.done ? <RiCheckLine className="h-4 w-4" /> : <step.icon className="h-4 w-4" />}
            </span>
            <p
              className={cn(
                "mt-3 text-sm font-semibold",
                step.done && "text-muted-foreground line-through decoration-[#C4C4BE]",
              )}
            >
              {step.title}
            </p>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{step.body}</p>
            {!step.done ? (
              <div className="mt-3 text-xs font-semibold text-primary hover:underline">
                {step.action}
              </div>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}
